import React, { useEffect, useMemo, useState } from 'react';
import { homeworkAPI, studentsAPI } from '../services/api';
import { useYear } from '../context/YearContext';

const inputStyle = {
  padding: '8px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 14,
};

export default function HomeworkAssignments() {
  const { year } = useYear();
  const [classes, setClasses] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ class: '', subject: '' });
  const [form, setForm] = useState({
    class: '',
    subject: '',
    title: '',
    description: '',
    assigned_date: new Date().toISOString().split('T')[0],
    due_date: '',
  });

  const loadItems = (activeFilters = filters) => {
    setLoading(true);
    homeworkAPI.getAll({
      year,
      class: activeFilters.class || undefined,
      subject: activeFilters.subject || undefined,
    })
      .then((res) => setItems(res.data.data || []))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load assignments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!year) return;
    studentsAPI.getClasses(year)
      .then((res) => setClasses(res.data.data || []))
      .catch(() => {});
  }, [year]);

  useEffect(() => {
    loadItems(filters);
  }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const subjects = useMemo(() => {
    const all = items.map((item) => item.subject).filter(Boolean);
    return [...new Set(all)].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await homeworkAPI.create({ ...form, academic_year: year });
      setForm((prev) => ({
        ...prev,
        subject: '',
        title: '',
        description: '',
        due_date: '',
      }));
      loadItems(filters);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save assignment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete homework "${item.title}"?`)) return;
    await homeworkAPI.delete(item.assignment_id);
    loadItems(filters);
  };

  return (
    <div className="page-content">
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Homework & Assignments</h2>
        <p style={{ margin: '6px 0 0', color: '#64748b' }}>
          Assign class-wise homework with subject, due date, and instructions.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card" style={{ padding: 18, marginBottom: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <select value={form.class} onChange={(e) => setForm((p) => ({ ...p, class: e.target.value }))} style={inputStyle} required>
            <option value="">Select class</option>
            {classes.map((cls) => <option key={cls} value={cls}>{cls}</option>)}
          </select>
          <input value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} placeholder="Subject" style={inputStyle} required />
          <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Homework title" style={inputStyle} required />
          <input type="date" value={form.assigned_date} onChange={(e) => setForm((p) => ({ ...p, assigned_date: e.target.value }))} style={inputStyle} lang="en-GB" />
          <input type="date" value={form.due_date} onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))} style={inputStyle} lang="en-GB" />
        </div>
        <textarea
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          placeholder="Instructions / assignment details"
          rows={4}
          style={{ ...inputStyle, width: '100%', marginTop: 12, resize: 'vertical' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 12, flexWrap: 'wrap' }}>
          {error ? <span style={{ color: '#dc2626', fontSize: 13 }}>{error}</span> : <span style={{ color: '#64748b', fontSize: 13 }}>Academic year: {year}</span>}
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Add Homework'}
          </button>
        </div>
      </form>

      <div className="card" style={{ padding: 18 }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <select
            value={filters.class}
            onChange={(e) => {
              const next = { ...filters, class: e.target.value };
              setFilters(next);
              loadItems(next);
            }}
            style={inputStyle}
          >
            <option value="">All classes</option>
            {classes.map((cls) => <option key={cls} value={cls}>{cls}</option>)}
          </select>
          <select
            value={filters.subject}
            onChange={(e) => {
              const next = { ...filters, subject: e.target.value };
              setFilters(next);
              loadItems(next);
            }}
            style={inputStyle}
          >
            <option value="">All subjects</option>
            {subjects.map((subject) => <option key={subject} value={subject}>{subject}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="loading">Loading assignments...</div>
        ) : items.length === 0 ? (
          <div className="empty-state"><p>No homework assignments found for the current filter.</p></div>
        ) : (
          <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Class</th>
                <th>Subject</th>
                <th>Title</th>
                <th>Assigned</th>
                <th>Due</th>
                <th>Description</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.assignment_id}>
                  <td><span className="class-badge">{item.class}</span></td>
                  <td>{item.subject}</td>
                  <td style={{ fontWeight: 600 }}>{item.title}</td>
                  <td>{item.assigned_date || '-'}</td>
                  <td>{item.due_date || '-'}</td>
                  <td style={{ maxWidth: 320, color: '#475569' }}>{item.description || '-'}</td>
                  <td>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(item)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
